import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";

const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    desc: "Perfect for side projects and learning.",
    cta: "Get started free",
    ctaStyle: "border border-[#dadce0] text-[#6C5CE7] hover:bg-[#fff4eb]",
    features: [
      { text: "1 project", included: true },
      { text: "Shared compute", included: true },
      { text: "Community support", included: true },
      { text: "Auto SSL", included: true },
      { text: "Custom domains", included: false },
      { text: "Team collaboration", included: false },
      { text: "Priority support", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    name: "Pro",
    price: "$20",
    period: "/month",
    desc: "For professionals and growing teams.",
    cta: "Start free trial",
    ctaStyle: "bg-[#6C5CE7] text-white hover:bg-[#e8872e] shadow-sm",
    popular: true,
    features: [
      { text: "Unlimited projects", included: true },
      { text: "Dedicated compute", included: true },
      { text: "Email support", included: true },
      { text: "Auto SSL", included: true },
      { text: "Custom domains", included: true },
      { text: "Team collaboration (up to 5)", included: true },
      { text: "Priority support", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    desc: "For organizations with advanced needs.",
    cta: "Contact us",
    ctaStyle: "border border-[#dadce0] text-[#6C5CE7] hover:bg-[#fff4eb]",
    features: [
      { text: "Unlimited projects", included: true },
      { text: "Dedicated compute", included: true },
      { text: "24/7 phone support", included: true },
      { text: "Auto SSL", included: true },
      { text: "Custom domains", included: true },
      { text: "Unlimited team members", included: true },
      { text: "Priority support", included: true },
      { text: "99.99% SLA guarantee", included: true },
    ],
  },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24 text-center">
          <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="pricing-title">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#5f6368] max-w-lg mx-auto">
            Start free. Scale when you're ready. No surprise bills.
          </p>
        </div>
      </div>

      {/* Tiers */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto" data-testid="pricing-tiers">
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              className={`relative rounded-xl border p-8 flex flex-col ${
                tier.popular
                  ? "border-[#6C5CE7] shadow-lg"
                  : "border-[#e8eaed]"
              }`}
              data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#6C5CE7] text-white text-[11px] font-bold tracking-wide uppercase rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className="text-[18px] font-medium text-[#202124]">{tier.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-[40px] font-normal text-[#202124]">{tier.price}</span>
                <span className="text-[14px] text-[#5f6368]">{tier.period}</span>
              </div>
              <p className="mt-3 text-[14px] text-[#5f6368]">{tier.desc}</p>

              <Link
                to={tier.name === "Enterprise" ? "/enterprise" : "#"}
                className={`mt-6 block text-center px-5 py-3 text-[14px] font-medium rounded-md transition-colors ${tier.ctaStyle}`}
              >
                {tier.cta}
              </Link>

              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-3">
                    {f.included ? (
                      <Check className="w-4 h-4 text-[#34a853] mt-0.5 shrink-0" />
                    ) : (
                      <Minus className="w-4 h-4 text-[#dadce0] mt-0.5 shrink-0" />
                    )}
                    <span className={`text-[13px] ${f.included ? "text-[#202124]" : "text-[#9aa0a6]"}`}>
                      {f.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ teaser */}
        <div className="mt-16 text-center">
          <p className="text-[14px] text-[#5f6368]">
            Have questions?{" "}
            <Link to="/enterprise" className="text-[#6C5CE7] hover:text-[#e8872e] font-medium">Talk to sales</Link>
            {" "}or check out our{" "}
            <Link to="/docs" className="text-[#6C5CE7] hover:text-[#e8872e] font-medium">documentation</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
